'use client'

import { BadgeCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usdCompact } from '../lib/format'
import { TokenIcon } from './token-select'

/// A 24h move in fomo's shape: a triangle carrying the direction, the
/// percentage beside it. The triangle is the non-colour cue (it points), the
/// colour is the at-a-glance scan, and a dash is the honest answer when the
/// feed has no figure.
export function Change({ v, className }: { v: number | null | undefined; className?: string }) {
	if (v === null || v === undefined || !Number.isFinite(v)) {
		return <span className={cn('text-muted-foreground', className)}>—</span>
	}
	const up = v >= 0
	return (
		<span
			aria-label={`${up ? 'up' : 'down'} ${Math.abs(v).toFixed(2)} percent in 24 hours`}
			className={cn(
				'inline-flex items-baseline gap-0.5 tabular-nums',
				up ? 'text-success-ink' : 'text-destructive-ink',
				className
			)}
		>
			<span aria-hidden="true" className="text-[0.7em] leading-none">
				{up ? '▲' : '▼'}
			</span>
			{Math.abs(v).toFixed(2)}%
		</span>
	)
}

/// The 40px roundel with the blue verified check overlapping its bottom-right
/// corner — the mark rides the ICON (fomo's anatomy), while impostor/ambiguous
/// warnings stay beside the name where the claim they dispute is.
export function BadgedIcon({
	token,
	verified,
	size = 40
}: {
	token: { id: string; name: string | null; icon: string | null }
	verified: boolean
	size?: number
}) {
	return (
		<span className="relative inline-flex shrink-0">
			<TokenIcon token={token} size={size} />
			{verified && (
				<span
					role="img"
					aria-label="Verified asset"
					className="absolute -bottom-0.5 -right-0.5 grid place-items-center rounded-full bg-background p-px"
				>
					<BadgeCheck className="size-4 fill-primary text-background" />
				</span>
			)}
		</span>
	)
}

/// The `[MC] $160.6M` chip: a small boxed "MC" tag with the market cap beside
/// it. Rendered only when the server sent a figure — an asset whose supply is
/// not cached yet gets NO chip rather than a made-up one.
export function McChip({ mc, className }: { mc: number | null | undefined; className?: string }) {
	if (mc === null || mc === undefined || !Number.isFinite(mc)) {
		return null
	}
	return (
		<span className={cn('inline-flex items-baseline gap-1 tabular-nums', className)}>
			<span className="rounded border border-border px-1 text-[9px] font-semibold leading-4 text-muted-foreground">
				MC
			</span>
			<span className="text-xs text-muted-foreground">{usdCompact(mc)}</span>
		</span>
	)
}
